import { useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import { FormInputField, lt } from '@mews/framework';
import { Typography, Stack, IconName, Title, Highlight, Tag } from '@optimus-web/core';
import { useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { TooltippedHighlightIcon } from '@mews-commander/ui';
import {
    GenerateContractFormStepName,
    GenerateContractFormValues,
    RecipientsFieldName,
    RecipientsFormFields,
} from '../types';

export const AddRecipientsSection = () => {
    const { getValues, unregister } = useFormContext<GenerateContractFormValues>();

    const formRecipients = getValues().recipients;
    const { selectedTemplates } = getValues().templates;

    const contractTemplateIds = Object.keys(selectedTemplates);
    const { data: recipientsData } = useRepositorySuspenseQuery('reservationContractTemplates', 'getRecipients', {
        contractTemplateIds,
    });
    const { recipients } = recipientsData;

    useEffect(() => {
        const newRoles = Object.keys(recipientsData?.recipients || {});
        const oldRoles = Object.keys(formRecipients || {});
        const removedRoles = oldRoles.filter((role) => !newRoles.includes(role));
        removedRoles.forEach((role) => {
            unregister(`${GenerateContractFormStepName.Recipients}.${RecipientsFormFields.Recipients}.${role}`);
        });
    }, [recipientsData, formRecipients, unregister]);

    return (
        <Stack vertical spacing="200">
            <Stack spacing="100">
                <Title level={2}>
                    {lt('ContractsGenerateContractRecipientsStepLabel')}
                    {UnicodeEntity.NoBreakSpace}
                    {recipients.length}
                </Title>
                <TooltippedHighlightIcon
                    value={IconName.AlertCircle}
                    size="small"
                    iconVariant="basic"
                    tooltip={lt('ContractsGenerateContractRecipientsTooltip')}
                />
            </Stack>
            {recipients.map((recipient, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Stack vertical spacing="100" key={`${recipient.role}-${index}`}>
                    <Stack spacing="100">
                        <Highlight size="small">{recipient.role}</Highlight>
                        <Tag variant="basicSubtle">{lt('ContractsGenerateContractRecipientsRole')}</Tag>
                    </Stack>
                    <Typography variant="secondary" textStyle="bodyMedium" as="div">
                        {lt('ContractsGenerateContractRecipientsFoundTemplates', {
                            templates: recipient.contractTemplates.map((template) => `"${template.name}"`).join(', '),
                        })}
                    </Typography>
                    <Stack distribution="stretch" spacing="200">
                        <FormInputField
                            defaultValue={recipient?.name || ''}
                            indicativeIcon={IconName.Profile}
                            name={`${GenerateContractFormStepName.Recipients}.${RecipientsFormFields.Recipients}.${recipient.role}.${RecipientsFieldName.Name}`}
                            label={lt('ContractsGenerateContractRecipientsNameLabel')}
                            required
                        />
                        <FormInputField
                            defaultValue={recipient?.email || ''}
                            indicativeIcon={IconName.Mails}
                            name={`${GenerateContractFormStepName.Recipients}.${RecipientsFormFields.Recipients}.${recipient.role}.${RecipientsFieldName.Email}`}
                            label={lt('ContractsGenerateContractRecipientsEmailLabel')}
                            required
                        />
                    </Stack>
                </Stack>
            ))}
        </Stack>
    );
};
